/*
 * @Author: Json Chen
 * @Date: 2021-07-05 15:49:11
 * @LastEditTime: 2022-06-13 11:33:32
 * @LastEditors: Json Chen
 * @Description:
 * @FilePath: /unifyxp-v2-store-portal/src/pages/SalesCannelVersion/SalesChannelSettings/SalesChannelCookies/index.tsx
 */
import CommonHeader from '@/src/components/CommonHeader';
import NoPermission from '@/src/components/NoPermission';
import { PreviewWrapper } from '@/src/constrains/SalesChannelPreviewPageComponents/PreviewContainer';
import { HomeLayout } from '@/src/layout/SalesChannelEditorLayout/HomeLayout';
import useBreadCrumbData from '@/src/pages/SalesCannelVersion/Hooks/useBreadCrumbData';
import {
  getCookieSetting,
  resetCookieSetting,
  setCookieSetting,
  postTranslateCookieSetting,
} from '@/src/request/salesChannel/setting/cookieSetting';
import { getLanguageSettingTranslation } from '@/src/request/salesChannel/setting/language';
import {
  isUserValid,
  salesChannelResourceNameGenerator,
} from '@/src/tools/getUserInfo';
import { getFieldFromTrans } from '@/src/tools/helper';
import {
  EnumCategory,
  EnumOperation,
  EnumSubCategory,
  EPermissionSource,
} from '@/src/types/common/resources';
import { useRequest } from 'ahooks';
import { Form, Spin } from 'antd';
import _ from 'lodash';
import React from 'react';
import { useSelector } from 'react-redux';
import { useHistory, useParams } from 'react-router-dom';
import SalesChannelHOC from 'src/constrains/SalesChannelHOC/SalesChannelHOC';
import {
  EnumMultiLanguageType,
  useQueryLanguageListContentEditor,
} from 'src/constrains/SalesChannelMultiLanguageSelect';
import AITranslationDropDown from 'src/constrains/SalesChannelAITranslation/AITranslationDropDown';
import MultiLangDropDown from 'src/constrains/SalesChannelMultiLanguageSelect/MultiLangDropDown';
import { isPrimaryLanguage } from 'src/constrains/SalesChannelMultiLanguageSelect/slice';
import { EnumSalesChannelMultiLanguage } from 'src/types/server/params';
import Preview from './Preview';
import Settings from './Settings';
import styles from './style.module.less';
import { useI18n } from 'src/hooks/useRouterChange';
import { i18nGet } from 'src/tools/tools';

type ResultData = {
  [key: string]: any;
};

const permissions = (uuid: string) => {
  const view_coolies_setting = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCookies,
        operation: EnumOperation.view_coolies_setting,
        uuid,
      }),
    ],
  });
  const edit_cookies_settings = isUserValid({
    source: EPermissionSource.salesChannel,
    resourcesGroup: [
      salesChannelResourceNameGenerator({
        category: EnumCategory.SalesChannelSettings,
        subCategory: EnumSubCategory.ChannelCookies,
        operation: EnumOperation.edit_cookies_settings,
        uuid,
      }),
    ],
  });

  return {
    view_coolies_setting,
    edit_cookies_settings,
  };
};

const SalesChannelCookies = (props) => {
  const i18n = useI18n();
  const getI18n = (str) => i18nGet(i18n, str);
  const [form] = Form.useForm();
  const currentLanguage = useQueryLanguageListContentEditor();
  const isOnPrimaryLanguage = useSelector(isPrimaryLanguage());

  const history = useHistory();
  const { uuid } = useParams<{ uuid: string }>();
  const { view_coolies_setting, edit_cookies_settings } = permissions(uuid);
  const { loading: breadCrumbLoading, breadcrumbData } = useBreadCrumbData({
    lastSecond: getI18n('general.sider.settings'),
    lastOne: getI18n('salesChannelCookies.index.cookies'),
  });
  const [translationEnabled, setTranslationEnabled] =
    React.useState<boolean>(false);
  const [cookiesUuid, setCookiesUuid] = React.useState('');
  const [onSaveDisable, setOnSaveDisable] = React.useState<boolean>(true);
  const [resultData, setResultData] = React.useState<ResultData>({});
  const [closeText, setCloseText] = React.useState<string>('');
  const [errList, setErrList] = React.useState<string[]>([]);

  const { loading: getCookieSettingLoading, run: _getCookieSetting } =
    useRequest((params) => getCookieSetting(uuid, params), {
      manual: true,
      ready: !!view_coolies_setting,
      onSuccess(result) {
        form.resetFields();
        const _resultData: ResultData = {};
        _resultData.enabled = result.enabled;
        _resultData.textColor = (result.style || {}).textColor;
        _resultData.backgroundColor = (result.style || {}).backgroundColor;
        _resultData.cookieContent = result.content || '';
        setTranslationEnabled(result?.translationEnabled || false);
        setCookiesUuid(result?.uuid);
        if (isOnPrimaryLanguage) {
          setResultData(result);
        }
        form.setFieldsValue(_resultData);
      },
    });
  const { loading: setCookieSettingLoading, run: _setCookieSetting } =
    useRequest(
      (params) =>
        setCookieSetting(
          {
            ...params,
          },
          {
            [EnumSalesChannelMultiLanguage.multiLanguageInParams]: true,
            [EnumSalesChannelMultiLanguage.multiLanguageForceUpdate]: true,
          },
        ),
      {
        manual: true,
        onSuccess(result) {
          setOnSaveDisable(true);
          // history.push(`/salesChannel/settings/${uuid}`);
        },
      },
    );
  const { loading: resetCookieSettingLoading, run: _resetCookieSetting } =
    useRequest(() => resetCookieSetting(uuid), {
      manual: true,
      onSuccess(result) {
        form.setFieldsValue({
          textColor: result.textColor,
          backgroundColor: result.backgroundColor,
        });
        if (onSaveDisable) {
          setOnSaveDisable(!onSaveDisable);
        }
      },
    });

  const { loading: translationLoading, run: _translateCookieSetting } =
    useRequest(
      (uuid, _translationEnabled) =>
        postTranslateCookieSetting(uuid, _translationEnabled, {
          [EnumSalesChannelMultiLanguage.multiLanguageInParams]: true,
          [EnumSalesChannelMultiLanguage.multiLanguageForceUpdate]: true,
        }),
      {
        manual: true,
        onSuccess(_, [, _translationEnabled]) {
          setTranslationEnabled(_translationEnabled);
        },
      },
    );

  const {
    loading: getLanguageSettingTranslationLoading,
    data: languagePackage,
  } = useRequest(
    () =>
      currentLanguage
        ? getLanguageSettingTranslation(uuid, currentLanguage)
        : Promise.resolve(),
    {
      cacheKey: 'languagePackage',
      refreshDeps: [currentLanguage],
      ready: !!view_coolies_setting,
    },
  );

  React.useEffect(() => {
    if (view_coolies_setting) {
      if (languagePackage) {
        const closeTextPaths = getFieldFromTrans(
          languagePackage,
          'closeCookies',
        );
        if (closeTextPaths[0]) {
          const closeText = _.get(
            languagePackage,
            closeTextPaths[0] + '.value',
            '',
          );
          setCloseText(closeText);
        }
      }
    }
  }, [languagePackage]);

  React.useEffect(() => {
    if (view_coolies_setting) {
      if (currentLanguage) {
        _getCookieSetting({
          [EnumSalesChannelMultiLanguage.multiLanguageInParams]: true,
        });
      }
    }
  }, [currentLanguage]);

  const onSubmit = () => {
    form
      .validateFields()
      .then((res) => {
        setErrList([]);
        _setCookieSetting({
          ...resultData,
          ...(isOnPrimaryLanguage
            ? {
                enabled: res.enabled,
                style: res.enabled
                  ? {
                      textColor: res.textColor,
                      backgroundColor: res.backgroundColor,
                    }
                  : null,
              }
            : {
                enabled: true,
              }),
          content: res.cookieContent,
        });
      })
      .catch((e) => {
        const { errorFields } = e;
        // 获取errList 的key
        setErrList(errorFields.reduce((p, c) => p.concat(c.name), []));
      });
  };
  const onGoBackClick = () => {
    history.push(`/salesChannel/settings/${uuid}`);
  };

  const onValuesChange = (__, allValues) => {
    if (onSaveDisable) {
      setOnSaveDisable(!onSaveDisable);
    }
  };
  const onCookiesStyleReset = () => {
    _resetCookieSetting();
  };
  const handleOnTranslate = (enabled) => {
    _translateCookieSetting(cookiesUuid, enabled);
  };
  const onCookiesStyleClone = () => {
    // _resetCookieSetting();
    setOnSaveDisable(false);
    _getCookieSetting({
      [EnumSalesChannelMultiLanguage.multiLanguageUsePrimaryLanguage]: true,
    });
  };
  return view_coolies_setting ? (
    <React.Fragment>
      <Spin
        spinning={
          resetCookieSettingLoading ||
          getCookieSettingLoading ||
          getLanguageSettingTranslationLoading ||
          setCookieSettingLoading ||
          breadCrumbLoading
        }
        wrapperClassName={styles.spinContainer}>
        <Form
          form={form}
          colon={false}
          onValuesChange={onValuesChange}
          className={styles.cookiesFormContainer}
          layout={'vertical'}>
          <CommonHeader
            breadcrumbData={breadcrumbData}
            onSaveDisable={onSaveDisable || !edit_cookies_settings}
            onSubmit={onSubmit}
            onDiscard={onGoBackClick}
            onGoBackClick={onGoBackClick}
            extraLeft={
              <>
                <MultiLangDropDown
                  hasModified={!onSaveDisable}
                  salesChannelUuidKey={'uuid'}
                  customType={EnumMultiLanguageType.SalesChannelSettingsCookies}
                />
                {handleOnTranslate && !isOnPrimaryLanguage && (
                  <div style={{ marginLeft: 8 }}>
                    <AITranslationDropDown
                      handleOnConfirm={handleOnTranslate}
                      translationEnabled={translationEnabled}
                      loading={translationLoading}
                    />
                  </div>
                )}
              </>
            }
          />

          <HomeLayout
            className={styles.homeLayoutContainer}
            leftChild={
              <PreviewWrapper>
                <Form.Item
                  noStyle
                  shouldUpdate={(p, c) => {
                    return !_.isEqual(p, c);
                  }}>
                  {({ getFieldsValue }) => {
                    // 获取所有字段的值
                    const fieldsValue = getFieldsValue(true);

                    return (
                      <Preview {...fieldsValue} closeTextI18n={closeText} />
                    );
                  }}
                </Form.Item>
              </PreviewWrapper>
            }
            rightChild={
              <Settings
                isOnPrimaryLanguage={isOnPrimaryLanguage}
                errList={errList}
                onReset={onCookiesStyleReset}
                onClone={onCookiesStyleClone}
                disableEdit={!edit_cookies_settings || translationEnabled}
              />
            }
          />
        </Form>
      </Spin>
    </React.Fragment>
  ) : (
    <NoPermission />
  );
};
export default SalesChannelHOC(SalesChannelCookies, {
  type: EnumMultiLanguageType.SalesChannelSettingsCookies,
});
